import { useEffect, useState, type ReactNode } from "react";

/** 화면 공통 조각. 규칙의 원천: harness/UI-SPEC.md §3·§5 */

/**
 * NULL 은 빈 칸 또는 `—` 로 표시한다. `0`·`미상` 같은 대체 텍스트를 넣지 않는다 —
 * 추정으로 오인된다 (UI-SPEC §5).
 */
export function Cell({ value }: { value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === "") {
    return <span className="empty">—</span>;
  }
  return <>{value}</>;
}

/** 상태와 결과는 별개의 배지다 — 하나로 합쳐 보여주지 않는다 (UI-SPEC §3) */
export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge status-${status}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function ResultBadge({ result }: { result: string | null }) {
  if (result === null) return <span className="empty">미정</span>;
  return <span className={`badge result-${result}`}>{RESULT_LABEL[result] ?? result}</span>;
}

const STATUS_LABEL: Record<string, string> = {
  RECEIVED: "입고",
  INSPECTING: "검수중",
  AT_VENDOR: "SECUI 체류",
  VENDOR_BACK: "SECUI 회수",
  READY_TO_RETURN: "반송 준비",
  RETURNED: "반송 완료",
};

const RESULT_LABEL: Record<string, string> = {
  OK: "자체 처리",
  TO_VENDOR: "SECUI 이관",
};

export const CASE_STATUS_OPTIONS = Object.entries(STATUS_LABEL);

export function Notice({
  kind = "info",
  children,
}: {
  kind?: "info" | "error" | "warn" | "ok";
  children: ReactNode;
}) {
  return <div className={`notice ${kind === "info" ? "" : kind}`}>{children}</div>;
}

/**
 * API 오류는 한글로 보여준다. 서버가 이미 사용자 언어로 message 를 주므로 그대로
 * 쓰고, 원본 코드는 작은 글씨로만 남긴다 (UI-SPEC §5).
 */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code: unknown }).code)
      : null;
  return (
    <Notice kind="error">
      {message} {code ? <span className="muted mono">({code})</span> : null}
    </Notice>
  );
}

/** 오래 걸리는 작업에는 진행 표시가 필수다 (UI-SPEC §5) */
export function Busy({ children = "불러오는 중…" }: { children?: ReactNode }) {
  return <p className="muted">{children}</p>;
}

export function useAsync<T>(
  run: () => Promise<T>,
  deps: unknown[],
): { data: T | null; error: unknown; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    run()
      .then((value) => alive && setData(value))
      .catch((err) => alive && setError(err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // deps 는 호출부가 명시한다 — run 을 의존성에 넣으면 매 렌더마다 다시 부른다
  }, [...deps, tick]);

  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

/** 확인 없이 되돌리기 어려운 동작을 하지 않는다 (UI-SPEC §5) */
export function confirmAction(message: string): boolean {
  return window.confirm(message);
}
